import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FaArrowLeft, FaCalendar, FaShareAlt, FaUser } from "react-icons/fa";
import { useDemoModal } from "../contexts/DemoModalContext";
import { fetchBlogPostBySlug, formatDate, getFeaturedImage } from "../lib/wordpressApi";
import { getImageLoading, handleImageError } from "../lib/imageOptimization";

const BlogDetail = () => {
  const { slug } = useParams();
  const { openModal } = useDemoModal();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  useEffect(() => {
    const loadPost = async () => {
      try {
        setLoading(true);
        setError(null);
        const fetched = await fetchBlogPostBySlug(slug, true);
        setPost(fetched);
      } catch (err) {
        setError(err.message || "Unable to load this post.");
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadPost();
    }
  }, [slug]);

  const featuredImage = useMemo(() => getFeaturedImage(post, "large"), [post]);
  const publishedDate = useMemo(() => formatDate(post?.date), [post]);

  const handleShare = () => {
    const shareUrl = window.location.href;
    const title = post?.title?.rendered || "The Lingua Alliance";

    if (navigator.share) {
      navigator.share({ title, text: title, url: shareUrl }).catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl).catch(() => {});
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-canvas pb-20 text-ink flex items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-canvas pb-20 text-ink flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg font-semibold text-ink mb-3">{error}</p>
          <Link
            to="/blogs"
            className="inline-flex items-center gap-2 text-primary font-semibold hover:text-primary/80 transition"
          >
            <FaArrowLeft /> Back to Blogs
          </Link>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-canvas pb-20 text-ink flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg font-semibold text-ink mb-3">Post not found.</p>
          <Link
            to="/blogs"
            className="inline-flex items-center gap-2 text-primary font-semibold hover:text-primary/80 transition"
          >
            <FaArrowLeft /> Back to Blogs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas pb-20 text-ink">
      <section className="relative overflow-hidden border-b border-primary/10 bg-gradient-to-b from-primary/5 to-white">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-transparent" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8 sm:py-10 md:py-12">
          <Link
            to="/blogs"
            className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-primary hover:text-primary/80 transition mb-4 sm:mb-6"
          >
            <FaArrowLeft /> Back to All Blogs
          </Link>

          <div>
            <span className="inline-flex rounded-full bg-primary/10 px-3 sm:px-4 py-1.5 sm:py-2 text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] sm:tracking-[0.25em] text-primary border border-primary/20 mb-3 sm:mb-4">
              {post?._embedded?.author?.[0]?.name || "The Lingua Alliance"}
            </span>
            <h1
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6"
              dangerouslySetInnerHTML={{ __html: post?.title?.rendered || "Untitled" }}
            />

            <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-ink-muted mb-6 sm:mb-8">
              <div className="flex items-center gap-2">
                <FaUser className="text-primary" />
                <span>{post?._embedded?.author?.[0]?.name || "The Lingua Alliance"}</span>
              </div>
              <div className="flex items-center gap-2">
                <FaCalendar className="text-primary" />
                <span>{publishedDate}</span>
              </div>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-2 text-primary font-semibold hover:text-primary/80 transition"
              >
                <FaShareAlt /> Share
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-2xl shadow-elevated border border-primary/10 bg-white -mt-8 sm:-mt-10 relative z-10">
          <div className="aspect-[16/9] sm:aspect-[21/9] bg-primary/5">
            {featuredImage ? (
              <img
                src={featuredImage}
                loading={getImageLoading(0)}
                alt={post?.title?.rendered || "Blog cover"}
                onError={(e) => handleImageError(e)}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-primary">No image</div>
            )}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 mt-10 sm:mt-12 lg:mt-14">
        <div className="bg-white rounded-2xl shadow-elevated border border-primary/10 overflow-hidden">
          <div className="prose prose-ink max-w-none p-6 sm:p-8 md:p-10 text-ink">
            <div
              className="prose prose-ink max-w-none"
              dangerouslySetInnerHTML={{ __html: post?.content?.rendered || "" }}
            />
          </div>

          <div className="border-t border-primary/10 p-6 sm:p-8 md:p-10 bg-primary/5">
            <div className="rounded-xl bg-white border border-primary/10 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-ink">Ready to master a new language?</h3>
                <p className="text-ink-muted text-sm sm:text-base">Book a free demo class and experience our teaching style first-hand.</p>
              </div>
              <button
                type="button"
                onClick={() => openModal("French")}
                className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-elevated transition hover:bg-primary/90"
              >
                Book Free Demo
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogDetail;
